import React, { useState, useEffect, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { selectCompanyDetails } from "store/company/reducer"
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Alert,
  Collapse,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
  Avatar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material"
import EditIcon from "@mui/icons-material/Edit"
import CloseIcon from "@mui/icons-material/Close"
import SaveIcon from "@mui/icons-material/Save"
import PhoneIcon from "@mui/icons-material/Phone"
import EmailIcon from "@mui/icons-material/Email"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import PaymentsIcon from "@mui/icons-material/Payments"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import AnalyticsIcon from "@mui/icons-material/Analytics"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import InventoryIcon from "@mui/icons-material/Inventory"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import PrintIcon from "@mui/icons-material/Print"
import FilterAltIcon from "@mui/icons-material/FilterAlt"
import ClearIcon from "@mui/icons-material/Clear"
import DownloadIcon from "@mui/icons-material/Download"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import Breadcrumbs from "@mui/material/Breadcrumbs"
import { Link } from "react-router-dom"

import { patchCustomer, getCustomerDetail, getSalesPersons, getCustOrders, getPaymentsByEntity, getLoansByCustomer } from "helpers/api_helper"
import { sumTotalOrder, countOrders } from "./reporting"
import MapModal from "./mapAddress"
import AddressModal from "./addresses"
import CustomFieldsPanel from "components/customfieldsPanel"

const MuiCustomerDetail = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()

  // Get company details from Redux for prints
  const companyDetails = useSelector(selectCompanyDetails)

  document.title = t("Customer") + " | FlexFlow ERP"

  const [isEditing, setIsEditing] = useState(false)
  const [customer, setCustomer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [orders, setOrders] = useState([])
  const [payments, setPayments] = useState([])
  const [loans, setLoans] = useState([])
  const [salespersonList, setSalespersonList] = useState([])
  const [salesperson, setSalesperson] = useState("")
  const [analyticsExpanded, setAnalyticsExpanded] = useState(true)
  const [ordersPage, setOrdersPage] = useState(0)
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10)
  const [paymentsPage, setPaymentsPage] = useState(0)
  const [paymentsRowsPerPage, setPaymentsRowsPerPage] = useState(10)
  const [loansPage, setLoansPage] = useState(0)
  const [loansRowsPerPage, setLoansRowsPerPage] = useState(10)
  const [fromDate, setFromDate] = useState("")
  const [toDate, setToDate] = useState("")

  useEffect(() => {
    getSalesPersons().then((res) => setSalespersonList(res || []))
  }, [])

  useEffect(() => {
    if (params?.id) {
      setLoading(true)
      Promise.all([
        getCustomerDetail(params.id),
        getCustOrders(params.id),
        getPaymentsByEntity(params.id),
        getLoansByCustomer(params.id)
      ]).then(([customerRes, ordersRes, paymentsRes, loansRes]) => {
        if (customerRes && customerRes[0]) {
          setCustomer(customerRes[0])
          setSalesperson(customerRes[0].salespersons)
        }
        setOrders(ordersRes || [])
        setPayments(paymentsRes || [])
        setLoans(loansRes || [])
        setLoading(false)
      })
    }
  }, [params])

  // Filter data by date range
  const filteredOrders = useMemo(() => {
    if (!fromDate && !toDate) return orders
    return orders.filter((order) => {
      const orderDate = new Date(order.created)
      if (fromDate && orderDate < new Date(fromDate)) return false
      if (toDate && orderDate > new Date(toDate + "T23:59:59")) return false
      return true
    })
  }, [orders, fromDate, toDate])

  const filteredPayments = useMemo(() => {
    if (!fromDate && !toDate) return payments
    return payments.filter((payment) => {
      const paymentDate = new Date(payment.date)
      if (fromDate && paymentDate < new Date(fromDate)) return false
      if (toDate && paymentDate > new Date(toDate + "T23:59:59")) return false
      return true
    })
  }, [payments, fromDate, toDate])

  const filteredLoans = useMemo(() => {
    if (!fromDate && !toDate) return loans
    return loans.filter((loan) => {
      const loanDate = new Date(loan.created)
      if (fromDate && loanDate < new Date(fromDate)) return false
      if (toDate && loanDate > new Date(toDate + "T23:59:59")) return false
      return true
    })
  }, [loans, fromDate, toDate])

  // Analytics calculations based on filtered data
  const analytics = useMemo(() => {
    const totalOrders = filteredOrders.length
    const offers = filteredOrders.filter((o) => o.status === "OFFER").length
    const pending = filteredOrders.filter((o) => ["INVOICED", "ON SHIPPING", "PENDING-STOCK"].includes(o.status)).length
    const shipped = filteredOrders.filter((o) => o.status === "SHIPPED").length

    // Sum sales from ALL filtered orders
    const sumSales = filteredOrders.reduce((acc, order) => {
      try {
        const totals = JSON.parse(order.totals || "{}")
        return acc + (Number(totals.totalOrder) || 0)
      } catch {
        return acc
      }
    }, 0)

    // Sum filtered payments
    const sumPayments = filteredPayments.reduce((acc, p) => acc + (Number(p.ammount) || 0), 0)

    // Loans analytics from filtered loans
    const loansOutbound = filteredLoans.filter((l) => l.bound === "OUTBOUND").reduce((acc, l) => acc + (Number(l.quantity) || 0), 0)
    const loansInbound = filteredLoans.filter((l) => l.bound === "INBOUND").reduce((acc, l) => acc + (Number(l.quantity) || 0), 0)
    const loansBalance = loansOutbound - loansInbound

    // Balance = Sales - Payments received
    const balance = sumSales - sumPayments

    return {
      totalOrders,
      offers,
      pending,
      shipped,
      sumSales,
      sumPayments,
      balance,
      paymentsCount: filteredPayments.length,
      loansCount: filteredLoans.length,
      loansOutbound,
      loansInbound,
      loansBalance,
    }
  }, [filteredOrders, filteredPayments, filteredLoans])

  const clearDateFilters = () => {
    setFromDate("")
    setToDate("")
  }

  // Print function
  const handlePrint = () => {
    const printWindow = window.open("", "_blank")
    const dateRange = fromDate || toDate
      ? `${fromDate ? new Date(fromDate).toLocaleDateString() : "..."} - ${toDate ? new Date(toDate).toLocaleDateString() : "..."}`
      : t("All Time")

    // Company info from Redux or defaults
    const companyName = companyDetails?.name || "FlexFlow ERP"
    const companyVat = companyDetails?.vat || ""
    const companyPhone = companyDetails?.phone || ""
    const companyEmail = companyDetails?.email || ""
    const companyAddress = companyDetails?.address || ""
    const companyCity = companyDetails?.city || ""
    const companyPostalCode = companyDetails?.postal_code || ""

    const ordersRows = filteredOrders.map((order) => {
      let total = 0
      try { total = JSON.parse(order.totals || "{}").totalOrder || 0 } catch {}
      return `
        <tr>
          <td>#${order.id}</td>
          <td>${order.created ? new Date(order.created).toLocaleDateString() : "-"}</td>
          <td>${order.status}</td>
          <td style="text-align: right;">${Number(total).toFixed(2)}€</td>
        </tr>
      `
    }).join("")

    const paymentsRows = filteredPayments.map((payment) => `
      <tr>
        <td>#${payment.id}</td>
        <td>${payment.date ? new Date(payment.date).toLocaleDateString() : "-"}</td>
        <td>${payment.type}</td>
        <td>${payment.bound}</td>
        <td style="text-align: right;">${Number(payment.ammount || 0).toFixed(2)}€</td>
      </tr>
    `).join("")

    const loansRows = filteredLoans.map((loan) => `
      <tr>
        <td>#${loan.id}</td>
        <td>${loan.created ? new Date(loan.created).toLocaleDateString() : "-"}</td>
        <td>${loan.prdname || "-"}</td>
        <td style="text-align: center;">${loan.quantity || 0}</td>
        <td>${loan.bound}</td>
      </tr>
    `).join("")

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t("Customer Report")} - ${customer.name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 11px; color: #1e293b; padding: 20px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; margin-bottom: 20px; }
          .company-info { }
          .company-name { font-size: 20px; font-weight: 700; color: #3b82f6; margin-bottom: 5px; }
          .company-details { font-size: 9px; color: #64748b; line-height: 1.5; }
          .report-info { text-align: right; }
          .report-info h2 { font-size: 14px; color: #1e293b; margin-bottom: 5px; }
          .report-info p { color: #64748b; font-size: 10px; }
          .section { margin-bottom: 20px; }
          .section-title { font-size: 12px; font-weight: 600; color: #3b82f6; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px; }
          .customer-info { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; background: #f8fafc; padding: 15px; border-radius: 6px; }
          .info-item label { display: block; font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px; }
          .info-item span { font-size: 11px; font-weight: 500; color: #1e293b; }
          .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
          .summary-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; text-align: center; }
          .summary-box .value { font-size: 18px; font-weight: 700; color: #1e293b; }
          .summary-box .value.green { color: #16a34a; }
          .summary-box .value.blue { color: #3b82f6; }
          .summary-box .value.orange { color: #d97706; }
          .summary-box .label { font-size: 9px; color: #64748b; text-transform: uppercase; margin-top: 3px; }
          table { width: 100%; border-collapse: collapse; font-size: 10px; }
          th { background: #f1f5f9; color: #475569; font-weight: 600; text-transform: uppercase; font-size: 9px; padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; }
          td { padding: 8px 10px; border-bottom: 1px solid #f1f5f9; }
          tr:nth-child(even) { background: #fafafa; }
          .totals-row { background: #f1f5f9 !important; font-weight: 600; }
          .totals-row td { border-top: 2px solid #e2e8f0; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e2e8f0; text-align: center; color: #94a3b8; font-size: 9px; }
          .no-data { text-align: center; padding: 20px; color: #94a3b8; font-style: italic; }
          @media print { body { padding: 10px; } .section { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-info">
            <div class="company-name">${companyName}</div>
            <div class="company-details">
              ${companyVat ? `${t("VAT")}: ${companyVat}<br>` : ""}
              ${companyAddress ? `${companyAddress}<br>` : ""}
              ${companyCity || companyPostalCode ? `${companyCity} ${companyPostalCode}<br>` : ""}
              ${companyPhone ? `${t("Tel")}: ${companyPhone}` : ""}${companyPhone && companyEmail ? " | " : ""}${companyEmail ? companyEmail : ""}
            </div>
          </div>
          <div class="report-info">
            <h2>${t("Customer Report")}</h2>
            <p>${t("Period")}: ${dateRange}</p>
            <p>${t("Generated")}: ${new Date().toLocaleString()}</p>
          </div>
        </div>

        <div class="section">
          <div class="section-title">${t("Customer Information")}</div>
          <div class="customer-info">
            <div class="info-item"><label>${t("Name")}</label><span>${customer.name || "-"}</span></div>
            <div class="info-item"><label>${t("Company")}</label><span>${customer.label || "-"}</span></div>
            <div class="info-item"><label>${t("Category")}</label><span>${customer.category || "-"}</span></div>
            <div class="info-item"><label>${t("Phone")}</label><span>${customer.phone || "-"}</span></div>
            <div class="info-item"><label>${t("Email")}</label><span>${customer.email || "-"}</span></div>
            <div class="info-item"><label>${t("VAT")}</label><span>${customer.vat || "-"}</span></div>
            <div class="info-item"><label>${t("Address")}</label><span>${customer.address || "-"}</span></div>
            <div class="info-item"><label>${t("City")}</label><span>${customer.city || "-"}${customer.postcode ? " " + customer.postcode : ""}</span></div>
            <div class="info-item"><label>${t("Tax Office")}</label><span>${customer.taxOffice || "-"}</span></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">${t("Summary")}</div>
          <div class="summary-grid">
            <div class="summary-box"><div class="value">${analytics.totalOrders}</div><div class="label">${t("Orders")}</div></div>
            <div class="summary-box"><div class="value green">${analytics.sumSales.toFixed(2)}€</div><div class="label">${t("Sum Sales")}</div></div>
            <div class="summary-box"><div class="value blue">${analytics.sumPayments.toFixed(2)}€</div><div class="label">${t("Sum Payments")}</div></div>
            <div class="summary-box"><div class="value ${analytics.balance > 0 ? "orange" : "green"}">${analytics.balance.toFixed(2)}€</div><div class="label">${t("Balance")}</div></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">${t("Orders")} (${filteredOrders.length})</div>
          ${filteredOrders.length > 0 ? `
            <table>
              <thead><tr><th>ID</th><th>${t("Date")}</th><th>${t("Status")}</th><th style="text-align: right;">${t("Total")}</th></tr></thead>
              <tbody>
                ${ordersRows}
                <tr class="totals-row">
                  <td colspan="3"><strong>${t("Total")}</strong></td>
                  <td style="text-align: right;"><strong>${analytics.sumSales.toFixed(2)}€</strong></td>
                </tr>
              </tbody>
            </table>
          ` : `<div class="no-data">${t("No orders found")}</div>`}
        </div>

        <div class="section">
          <div class="section-title">${t("Payments")} (${filteredPayments.length})</div>
          ${filteredPayments.length > 0 ? `
            <table>
              <thead><tr><th>ID</th><th>${t("Date")}</th><th>${t("Type")}</th><th>${t("Direction")}</th><th style="text-align: right;">${t("Amount")}</th></tr></thead>
              <tbody>
                ${paymentsRows}
                <tr class="totals-row">
                  <td colspan="4"><strong>${t("Total")}</strong></td>
                  <td style="text-align: right;"><strong>${analytics.sumPayments.toFixed(2)}€</strong></td>
                </tr>
              </tbody>
            </table>
          ` : `<div class="no-data">${t("No payments found")}</div>`}
        </div>

        <div class="section">
          <div class="section-title">${t("Loans")} (${filteredLoans.length})</div>
          ${filteredLoans.length > 0 ? `
            <table>
              <thead><tr><th>ID</th><th>${t("Date")}</th><th>${t("Product")}</th><th style="text-align: center;">${t("Qty")}</th><th>${t("Direction")}</th></tr></thead>
              <tbody>
                ${loansRows}
                <tr class="totals-row">
                  <td colspan="3"><strong>${t("Balance")}</strong></td>
                  <td style="text-align: center;"><strong>${analytics.loansBalance}</strong></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          ` : `<div class="no-data">${t("No loans found")}</div>`}
        </div>

        <div class="footer">
          <p>${companyName} - ${t("Customer Report")} - ${customer.name}</p>
        </div>
      </body>
      </html>
    `

    printWindow.document.write(html)
    printWindow.document.close()
    printWindow.onload = () => {
      printWindow.print()
    }
  }

  // Generate PDF HTML content
  const generatePDFContent = () => {
    const dateRange = fromDate || toDate
      ? `${fromDate ? new Date(fromDate).toLocaleDateString() : "..."} - ${toDate ? new Date(toDate).toLocaleDateString() : "..."}`
      : t("All Time")

    // Company info from Redux or defaults
    const companyName = companyDetails?.name || "FlexFlow ERP"
    const companyVat = companyDetails?.vat || ""
    const companyPhone = companyDetails?.phone || ""
    const companyEmail = companyDetails?.email || ""
    const companyAddress = companyDetails?.address || ""
    const companyCity = companyDetails?.city || ""
    const companyPostalCode = companyDetails?.postal_code || ""

    const ordersRows = filteredOrders.map((order) => {
      let total = 0
      try { total = JSON.parse(order.totals || "{}").totalOrder || 0 } catch {}
      return `
        <tr>
          <td>#${order.id}</td>
          <td>${order.created ? new Date(order.created).toLocaleDateString() : "-"}</td>
          <td>${order.status}</td>
          <td style="text-align: right;">${Number(total).toFixed(2)}€</td>
        </tr>
      `
    }).join("")

    const paymentsRows = filteredPayments.map((payment) => `
      <tr>
        <td>#${payment.id}</td>
        <td>${payment.date ? new Date(payment.date).toLocaleDateString() : "-"}</td>
        <td>${payment.type}</td>
        <td>${payment.bound}</td>
        <td style="text-align: right;">${Number(payment.ammount || 0).toFixed(2)}€</td>
      </tr>
    `).join("")

    const loansRows = filteredLoans.map((loan) => `
      <tr>
        <td>#${loan.id}</td>
        <td>${loan.created ? new Date(loan.created).toLocaleDateString() : "-"}</td>
        <td>${loan.prdname || "-"}</td>
        <td style="text-align: center;">${loan.quantity || 0}</td>
        <td>${loan.bound}</td>
      </tr>
    `).join("")

    return `
      <div id="pdf-content" style="padding: 20px; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; font-size: 11px; color: #1e293b; background: white; width: 800px;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #3b82f6; padding-bottom: 15px; margin-bottom: 20px;">
          <div>
            <div style="font-size: 20px; font-weight: 700; color: #3b82f6; margin-bottom: 5px;">${companyName}</div>
            <div style="font-size: 9px; color: #64748b; line-height: 1.5;">
              ${companyVat ? `${t("VAT")}: ${companyVat}<br>` : ""}
              ${companyAddress ? `${companyAddress}<br>` : ""}
              ${companyCity || companyPostalCode ? `${companyCity} ${companyPostalCode}<br>` : ""}
              ${companyPhone ? `${t("Tel")}: ${companyPhone}` : ""}${companyPhone && companyEmail ? " | " : ""}${companyEmail ? companyEmail : ""}
            </div>
          </div>
          <div style="text-align: right;">
            <h2 style="font-size: 14px; color: #1e293b; margin: 0 0 5px 0;">${t("Customer Report")}</h2>
            <p style="color: #64748b; font-size: 10px; margin: 0;">${t("Period")}: ${dateRange}</p>
            <p style="color: #64748b; font-size: 10px; margin: 0;">${t("Generated")}: ${new Date().toLocaleString()}</p>
          </div>
        </div>

        <div style="margin-bottom: 20px;">
          <div style="font-size: 12px; font-weight: 600; color: #3b82f6; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px;">${t("Customer Information")}</div>
          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; background: #f8fafc; padding: 15px; border-radius: 6px;">
            <div><div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px;">${t("Name")}</div><div style="font-size: 11px; font-weight: 500;">${customer.name || "-"}</div></div>
            <div><div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px;">${t("Company")}</div><div style="font-size: 11px; font-weight: 500;">${customer.label || "-"}</div></div>
            <div><div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px;">${t("Category")}</div><div style="font-size: 11px; font-weight: 500;">${customer.category || "-"}</div></div>
            <div><div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px;">${t("Phone")}</div><div style="font-size: 11px; font-weight: 500;">${customer.phone || "-"}</div></div>
            <div><div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px;">${t("Email")}</div><div style="font-size: 11px; font-weight: 500;">${customer.email || "-"}</div></div>
            <div><div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px;">${t("VAT")}</div><div style="font-size: 11px; font-weight: 500;">${customer.vat || "-"}</div></div>
            <div><div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px;">${t("Address")}</div><div style="font-size: 11px; font-weight: 500;">${customer.address || "-"}</div></div>
            <div><div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px;">${t("City")}</div><div style="font-size: 11px; font-weight: 500;">${customer.city || "-"}${customer.postcode ? " " + customer.postcode : ""}</div></div>
            <div><div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-bottom: 2px;">${t("Tax Office")}</div><div style="font-size: 11px; font-weight: 500;">${customer.taxOffice || "-"}</div></div>
          </div>
        </div>

        <div style="margin-bottom: 20px;">
          <div style="font-size: 12px; font-weight: 600; color: #3b82f6; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px;">${t("Summary")}</div>
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;">
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; text-align: center;">
              <div style="font-size: 18px; font-weight: 700; color: #1e293b;">${analytics.totalOrders}</div>
              <div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-top: 3px;">${t("Orders")}</div>
            </div>
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; text-align: center;">
              <div style="font-size: 18px; font-weight: 700; color: #16a34a;">${analytics.sumSales.toFixed(2)}€</div>
              <div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-top: 3px;">${t("Sum Sales")}</div>
            </div>
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; text-align: center;">
              <div style="font-size: 18px; font-weight: 700; color: #3b82f6;">${analytics.sumPayments.toFixed(2)}€</div>
              <div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-top: 3px;">${t("Sum Payments")}</div>
            </div>
            <div style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px; text-align: center;">
              <div style="font-size: 18px; font-weight: 700; color: ${analytics.balance > 0 ? "#d97706" : "#16a34a"};">${analytics.balance.toFixed(2)}€</div>
              <div style="font-size: 9px; color: #64748b; text-transform: uppercase; margin-top: 3px;">${t("Balance")}</div>
            </div>
          </div>
        </div>

        <div style="margin-bottom: 20px;">
          <div style="font-size: 12px; font-weight: 600; color: #3b82f6; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px;">${t("Orders")} (${filteredOrders.length})</div>
          ${filteredOrders.length > 0 ? `
            <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
              <thead><tr style="background: #f1f5f9;">
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">ID</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Date")}</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Status")}</th>
                <th style="padding: 8px 10px; text-align: right; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Total")}</th>
              </tr></thead>
              <tbody>
                ${ordersRows}
                <tr style="background: #f1f5f9; font-weight: 600;">
                  <td colspan="3" style="padding: 8px 10px; border-top: 2px solid #e2e8f0;"><strong>${t("Total")}</strong></td>
                  <td style="padding: 8px 10px; text-align: right; border-top: 2px solid #e2e8f0;"><strong>${analytics.sumSales.toFixed(2)}€</strong></td>
                </tr>
              </tbody>
            </table>
          ` : `<div style="text-align: center; padding: 20px; color: #94a3b8; font-style: italic;">${t("No orders found")}</div>`}
        </div>

        <div style="margin-bottom: 20px;">
          <div style="font-size: 12px; font-weight: 600; color: #3b82f6; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px;">${t("Payments")} (${filteredPayments.length})</div>
          ${filteredPayments.length > 0 ? `
            <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
              <thead><tr style="background: #f1f5f9;">
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">ID</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Date")}</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Type")}</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Direction")}</th>
                <th style="padding: 8px 10px; text-align: right; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Amount")}</th>
              </tr></thead>
              <tbody>
                ${paymentsRows}
                <tr style="background: #f1f5f9; font-weight: 600;">
                  <td colspan="4" style="padding: 8px 10px; border-top: 2px solid #e2e8f0;"><strong>${t("Total")}</strong></td>
                  <td style="padding: 8px 10px; text-align: right; border-top: 2px solid #e2e8f0;"><strong>${analytics.sumPayments.toFixed(2)}€</strong></td>
                </tr>
              </tbody>
            </table>
          ` : `<div style="text-align: center; padding: 20px; color: #94a3b8; font-style: italic;">${t("No payments found")}</div>`}
        </div>

        <div style="margin-bottom: 20px;">
          <div style="font-size: 12px; font-weight: 600; color: #3b82f6; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 10px;">${t("Loans")} (${filteredLoans.length})</div>
          ${filteredLoans.length > 0 ? `
            <table style="width: 100%; border-collapse: collapse; font-size: 10px;">
              <thead><tr style="background: #f1f5f9;">
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">ID</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Date")}</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Product")}</th>
                <th style="padding: 8px 10px; text-align: center; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Qty")}</th>
                <th style="padding: 8px 10px; text-align: left; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #475569; text-transform: uppercase; font-size: 9px;">${t("Direction")}</th>
              </tr></thead>
              <tbody>
                ${loansRows}
                <tr style="background: #f1f5f9; font-weight: 600;">
                  <td colspan="3" style="padding: 8px 10px; border-top: 2px solid #e2e8f0;"><strong>${t("Balance")}</strong></td>
                  <td style="padding: 8px 10px; text-align: center; border-top: 2px solid #e2e8f0;"><strong>${analytics.loansBalance}</strong></td>
                  <td style="padding: 8px 10px; border-top: 2px solid #e2e8f0;"></td>
                </tr>
              </tbody>
            </table>
          ` : `<div style="text-align: center; padding: 20px; color: #94a3b8; font-style: italic;">${t("No loans found")}</div>`}
        </div>

        <div style="margin-top: 30px; padding-top: 15px; border-top: 1px solid #e2e8f0; text-align: center; color: #94a3b8; font-size: 9px;">
          <p>${companyName} - ${t("Customer Report")} - ${customer.name}</p>
        </div>
      </div>
    `
  }

  // Download PDF function
  const handleDownloadPDF = async () => {
    const content = generatePDFContent()

    // Create temporary container
    const container = document.createElement("div")
    container.innerHTML = content
    container.style.position = "absolute"
    container.style.left = "-9999px"
    container.style.top = "0"
    document.body.appendChild(container)

    try {
      const element = container.querySelector("#pdf-content")
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff"
      })

      const imgData = canvas.toDataURL("image/png")
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      })

      const imgWidth = 210 // A4 width in mm
      const pageHeight = 297 // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight
      let position = 0

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      // Add more pages if content is longer than one page
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight
        pdf.addPage()
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      const fileName = `FlexFlow-Customer-${customer.name.replace(/\s+/g, "-")}-${new Date().toISOString().split("T")[0]}.pdf`
      pdf.save(fileName)
    } finally {
      document.body.removeChild(container)
    }
  }

  // Email function
  const handleSendEmail = () => {
    const dateRange = fromDate || toDate
      ? `${fromDate ? new Date(fromDate).toLocaleDateString() : "..."} - ${toDate ? new Date(toDate).toLocaleDateString() : "..."}`
      : t("All Time")

    const subject = encodeURIComponent(`${t("Customer Report")} - ${customer.name} - FlexFlow ERP`)
    const body = encodeURIComponent(`${t("Customer Report")}

${t("Customer")}: ${customer.name}
${t("Company")}: ${customer.label || "-"}
${t("Period")}: ${dateRange}

${t("Summary")}:
- ${t("Orders")}: ${analytics.totalOrders}
- ${t("Sum Sales")}: ${analytics.sumSales.toFixed(2)}€
- ${t("Sum Payments")}: ${analytics.sumPayments.toFixed(2)}€
- ${t("Balance")}: ${analytics.balance.toFixed(2)}€
- ${t("Loans Balance")}: ${analytics.loansBalance}

${t("Contact")}:
- ${t("Phone")}: ${customer.phone || "-"}
- ${t("Email")}: ${customer.email || "-"}
- ${t("Address")}: ${customer.address || "-"}, ${customer.city || ""} ${customer.postcode || ""}

---
${t("Generated by FlexFlow ERP")}
${new Date().toLocaleString()}

${t("Note")}: ${t("Please download and attach the PDF report for detailed information.")}
`)

    // Open email client
    window.location.href = `mailto:${customer.email || ""}?subject=${subject}&body=${body}`
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setCustomer((prev) => ({ ...prev, [name]: value }))
  }

  const updateCustomer = () => {
    const payload = { resource: [customer] }
    patchCustomer(payload).then((res) => {
      if (res?.resource?.[0]) {
        setIsEditing(false)
      }
    })
  }

  const updateCustomFields = (fields) => {
    setCustomer((prev) => ({ ...prev, customfields: JSON.stringify(fields) }))
  }

  const updateExtraAddresses = (newAddresses) => {
    setCustomer((prev) => ({ ...prev, extra_address: JSON.stringify(newAddresses) }))
  }

  const parseCustomFields = (customfields) => {
    try {
      return customfields ? JSON.parse(customfields) : []
    } catch {
      return []
    }
  }

  const getStatusStyle = (status) => {
    const styles = {
      OFFER: { bg: "#dbeafe", color: "#1e40af" },
      INVOICED: { bg: "#dcfce7", color: "#166534" },
      "ON SHIPPING": { bg: "#fef3c7", color: "#92400e" },
      "PENDING-STOCK": { bg: "#fee2e2", color: "#991b1b" },
      SHIPPED: { bg: "#d1fae5", color: "#065f46" },
    }
    return styles[status] || { bg: "#f3f4f6", color: "#374151" }
  }

  const getPaymentTypeStyle = (type) => {
    const styles = {
      CASH: { bg: "#dcfce7", color: "#166534" },
      CHECK: { bg: "#fef3c7", color: "#92400e" },
      BANK: { bg: "#dbeafe", color: "#1e40af" },
    }
    return styles[type] || { bg: "#f3f4f6", color: "#374151" }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (!customer) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{t("Customer not found")}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Compact Header */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => navigate("/customers")}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ fontSize: "0.875rem" }}>
              <Link to="/customers" style={{ color: "#6b7280", textDecoration: "none" }}>
                {t("Customers")}
              </Link>
              <Typography color="text.primary" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                {customer.name}
              </Typography>
            </Breadcrumbs>
            {customer.category && (
              <Chip label={customer.category} size="small" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#eff6ff", color: "#3b82f6" }} />
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Date Filters */}
            <Stack direction="row" spacing={1} alignItems="center">
              <FilterAltIcon sx={{ fontSize: 18, color: "#64748b" }} />
              <TextField
                type="date"
                size="small"
                label={t("From")}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 140, "& .MuiInputBase-input": { fontSize: "0.8125rem", py: 0.75 } }}
              />
              <TextField
                type="date"
                size="small"
                label={t("To")}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 140, "& .MuiInputBase-input": { fontSize: "0.8125rem", py: 0.75 } }}
              />
              {(fromDate || toDate) && (
                <IconButton size="small" onClick={clearDateFilters} sx={{ color: "#64748b" }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
            {/* Print Button */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{ textTransform: "none", fontSize: "0.8125rem" }}
            >
              {t("Print")}
            </Button>
            {/* Download PDF Button */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPDF}
              sx={{ textTransform: "none", fontSize: "0.8125rem" }}
            >
              PDF
            </Button>
            {/* Email Button */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<MailOutlineIcon />}
              onClick={handleSendEmail}
              sx={{ textTransform: "none", fontSize: "0.8125rem" }}
            >
              {t("Email")}
            </Button>
            {/* Edit Button */}
            <Button
              size="small"
              variant={isEditing ? "outlined" : "contained"}
              startIcon={isEditing ? <CloseIcon /> : <EditIcon />}
              onClick={() => setIsEditing(!isEditing)}
              sx={{ textTransform: "none", fontSize: "0.8125rem" }}
            >
              {isEditing ? t("Cancel") : t("Edit")}
            </Button>
            {isEditing && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={updateCustomer}
                sx={{ textTransform: "none", fontSize: "0.8125rem" }}
              >
                {t("Save")}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Collapsible Analytics Panel - Right after header */}
      <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <Box
          onClick={() => setAnalyticsExpanded(!analyticsExpanded)}
          sx={{
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            bgcolor: analyticsExpanded ? "#f8fafc" : "transparent",
            "&:hover": { bgcolor: "#f8fafc" },
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AnalyticsIcon sx={{ fontSize: 18, color: "#64748b" }} />
            <Typography variant="body2" fontWeight={500} color="#475569">
              {t("Analytics")}
            </Typography>
          </Stack>
          {analyticsExpanded ? <ExpandLessIcon sx={{ color: "#64748b" }} /> : <ExpandMoreIcon sx={{ color: "#64748b" }} />}
        </Box>
        <Collapse in={analyticsExpanded}>
          <Box sx={{ p: 2, borderTop: "1px solid #e2e8f0" }}>
            <Grid container spacing={2}>
              {/* Orders Stats */}
              <Grid item xs={6} sm={4} md={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 36, height: 36 }}>
                    <ShoppingCartIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1e293b">{analytics.totalOrders}</Typography>
                    <Typography variant="caption" color="#64748b">{t("Orders")}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={4} md={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#e0e7ff", color: "#4f46e5", width: 36, height: 36 }}>
                    <InventoryIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1e293b">{analytics.offers}</Typography>
                    <Typography variant="caption" color="#64748b">{t("Offers")}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={4} md={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#fef3c7", color: "#d97706", width: 36, height: 36 }}>
                    <LocalShippingIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1e293b">{analytics.pending}</Typography>
                    <Typography variant="caption" color="#64748b">{t("Pending")}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={4} md={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a", width: 36, height: 36 }}>
                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#1e293b">{analytics.shipped}</Typography>
                    <Typography variant="caption" color="#64748b">{t("Shipped")}</Typography>
                  </Box>
                </Stack>
              </Grid>
              {/* Financial Stats */}
              <Grid item xs={6} sm={4} md={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a", width: 36, height: 36 }}>
                    <TrendingUpIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#16a34a">{analytics.sumSales.toFixed(2)}€</Typography>
                    <Typography variant="caption" color="#64748b">{t("Sum Sales")}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={4} md={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 36, height: 36 }}>
                    <PaymentsIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} color="#3b82f6">{analytics.sumPayments.toFixed(2)}€</Typography>
                    <Typography variant="caption" color="#64748b">{t("Sum Payments")}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={4} md={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: analytics.balance > 0 ? "#fef3c7" : "#dcfce7", color: analytics.balance > 0 ? "#d97706" : "#16a34a", width: 36, height: 36 }}>
                    <AccountBalanceWalletIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: analytics.balance > 0 ? "#d97706" : "#16a34a" }}>
                      {analytics.balance.toFixed(2)}€
                    </Typography>
                    <Typography variant="caption" color="#64748b">{t("Balance")}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={6} sm={4} md={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar sx={{ bgcolor: analytics.loansBalance > 0 ? "#fef3c7" : "#dcfce7", color: analytics.loansBalance > 0 ? "#d97706" : "#16a34a", width: 36, height: 36 }}>
                    <SwapHorizIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: analytics.loansBalance > 0 ? "#d97706" : "#16a34a" }}>
                      {analytics.loansBalance}
                    </Typography>
                    <Typography variant="caption" color="#64748b">{t("Loans")}</Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Customer Details Section - All Information */}
      <Paper elevation={0} sx={{ p: 2, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
            {t("Customer Information")}
          </Typography>
        </Box>
        {!isEditing ? (
          <Grid container spacing={2}>
            {/* Row 1: Name, Company, Category */}
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Name")}</Typography>
              <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.name || "-"}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Company")}</Typography>
              <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.label || "-"}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Category")}</Typography>
              <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.category || "-"}</Typography>
            </Grid>
            {/* Row 2: Phone, Phone 2, Email */}
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Phone")}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <PhoneIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.phone || "-"}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Phone 2")}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <PhoneIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.phone2 || "-"}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Email")}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <EmailIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.email || "-"}</Typography>
              </Stack>
            </Grid>
            {/* Row 3: Address, City, Postcode */}
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Address")}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LocationOnIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.address || "-"}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("City")}</Typography>
              <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.city || "-"}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Postcode")}</Typography>
              <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.postcode || "-"}</Typography>
            </Grid>
            {/* Row 4: VAT, Tax Office, Profession */}
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("VAT")}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <ReceiptLongIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
                <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.vat || "-"}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Tax Office")}</Typography>
              <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.taxOffice || "-"}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="#64748b" sx={{ textTransform: "uppercase", fontSize: "0.65rem" }}>{t("Profession")}</Typography>
              <Typography variant="body2" fontWeight={500} color="#1e293b">{customer.profession || "-"}</Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label={t("Name")} name="name" value={customer.name || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label={t("Company")} name="label" value={customer.label || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label={t("Address")} name="address" value={customer.address || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label={t("City")} name="city" value={customer.city || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label={t("Postcode")} name="postcode" value={customer.postcode || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label={t("Phone")} name="phone" value={customer.phone || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label={t("Phone 2")} name="phone2" value={customer.phone2 || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label={t("Email")} name="email" value={customer.email || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label={t("VAT")} name="vat" value={customer.vat || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label={t("Tax Office")} name="taxOffice" value={customer.taxOffice || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label={t("Category")} name="category" value={customer.category || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label={t("Profession")} name="profession" value={customer.profession || ""} onChange={handleInputChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("Salesperson")}</InputLabel>
                <Select
                  value={salesperson || ""}
                  label={t("Salesperson")}
                  onChange={(e) => {
                    setSalesperson(e.target.value)
                    setCustomer((prev) => ({ ...prev, salespersons: e.target.value }))
                  }}
                >
                  {salespersonList?.map((person, i) => (
                    <MenuItem key={i} value={person.id}>{person.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <MapModal name="address" address={customer.address} onAddressSelect={(a) => setCustomer((prev) => ({ ...prev, address: a }))} />
                <AddressModal onSave={updateExtraAddresses} addresses={customer?.extra_address ? JSON.parse(customer.extra_address) : []} />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <CustomFieldsPanel customFields={parseCustomFields(customer?.customfields)} onSave={updateCustomFields} />
            </Grid>
          </Grid>
        )}
      </Paper>

      {/* Orders List */}
      <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ShoppingCartIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
            <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
              {t("Orders")}
            </Typography>
            <Chip label={filteredOrders.length} size="small" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#eff6ff", color: "#3b82f6" }} />
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Date")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Items")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Total")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Status")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#9ca3af" }}>
                    {t("No orders found")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrders
                  .slice(ordersPage * ordersRowsPerPage, ordersPage * ordersRowsPerPage + ordersRowsPerPage)
                  .map((order) => {
                    let itemsCount = 0
                    let total = 0
                    try {
                      itemsCount = JSON.parse(order.products || "[]").length
                      total = JSON.parse(order.totals || "{}").totalOrder || 0
                    } catch {}
                    const statusStyle = getStatusStyle(order.status)
                    return (
                      <TableRow
                        key={order.id}
                        hover
                        onClick={() => navigate(`/order-detail/${order.id}`)}
                        sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" } }}
                      >
                        <TableCell sx={{ fontSize: "0.8125rem", color: "#3b82f6", fontWeight: 500 }}>#{order.id}</TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>
                          {order.created ? new Date(order.created).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>{itemsCount}</TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>{Number(total).toFixed(2)}€</TableCell>
                        <TableCell>
                          <Chip
                            label={order.status}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 500,
                              bgcolor: statusStyle.bg,
                              color: statusStyle.color,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredOrders.length > 0 && (
          <TablePagination
            component="div"
            count={filteredOrders.length}
            page={ordersPage}
            onPageChange={(e, newPage) => setOrdersPage(newPage)}
            rowsPerPage={ordersRowsPerPage}
            onRowsPerPageChange={(e) => {
              setOrdersRowsPerPage(parseInt(e.target.value, 10))
              setOrdersPage(0)
            }}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{ borderTop: "1px solid #e2e8f0" }}
          />
        )}
      </Paper>

      {/* Payments List */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PaymentsIcon sx={{ fontSize: 18, color: "#16a34a" }} />
            <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
              {t("Payments")}
            </Typography>
            <Chip label={filteredPayments.length} size="small" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#dcfce7", color: "#16a34a" }} />
          </Stack>
          <Typography variant="body2" color="#64748b">
            {t("Total")}: <strong style={{ color: "#16a34a" }}>{analytics.sumPayments.toFixed(2)}€</strong>
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Date")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Type")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Amount")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Direction")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#9ca3af" }}>
                    {t("No payments found")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments
                  .slice(paymentsPage * paymentsRowsPerPage, paymentsPage * paymentsRowsPerPage + paymentsRowsPerPage)
                  .map((payment) => {
                    const typeStyle = getPaymentTypeStyle(payment.type)
                    const isInbound = payment.bound === "Inbound"
                    return (
                      <TableRow
                        key={payment.id}
                        hover
                        onClick={() => navigate(`/payment-overview/${payment.id}`)}
                        sx={{ cursor: "pointer", "&:hover": { bgcolor: "#f8fafc" } }}
                      >
                        <TableCell sx={{ fontSize: "0.8125rem", color: "#3b82f6", fontWeight: 500 }}>#{payment.id}</TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>
                          {payment.date ? new Date(payment.date).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.type}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 500,
                              bgcolor: typeStyle.bg,
                              color: typeStyle.color,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", fontWeight: 500, color: isInbound ? "#16a34a" : "#dc2626" }}>
                          {isInbound ? "+" : "-"}{Number(payment.ammount || 0).toFixed(2)}€
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.bound}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 500,
                              bgcolor: isInbound ? "#dcfce7" : "#fee2e2",
                              color: isInbound ? "#16a34a" : "#dc2626",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredPayments.length > 0 && (
          <TablePagination
            component="div"
            count={filteredPayments.length}
            page={paymentsPage}
            onPageChange={(e, newPage) => setPaymentsPage(newPage)}
            rowsPerPage={paymentsRowsPerPage}
            onRowsPerPageChange={(e) => {
              setPaymentsRowsPerPage(parseInt(e.target.value, 10))
              setPaymentsPage(0)
            }}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{ borderTop: "1px solid #e2e8f0" }}
          />
        )}
      </Paper>

      {/* Loans List */}
      <Paper elevation={0} sx={{ mt: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SwapHorizIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
            <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
              {t("Loans")}
            </Typography>
            <Chip label={filteredLoans.length} size="small" sx={{ height: 20, fontSize: "0.7rem", bgcolor: "#ede9fe", color: "#8b5cf6" }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Typography variant="body2" color="#64748b">
              {t("Out")}: <strong style={{ color: "#d97706" }}>{analytics.loansOutbound}</strong>
            </Typography>
            <Typography variant="body2" color="#64748b">
              {t("In")}: <strong style={{ color: "#16a34a" }}>{analytics.loansInbound}</strong>
            </Typography>
            <Typography variant="body2" color="#64748b">
              {t("Balance")}: <strong style={{ color: analytics.loansBalance > 0 ? "#d97706" : "#16a34a" }}>{analytics.loansBalance}</strong>
            </Typography>
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>ID</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Date")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Product")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Quantity")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", fontSize: "0.75rem", textTransform: "uppercase" }}>{t("Direction")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLoans.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: "#9ca3af" }}>
                    {t("No loans found")}
                  </TableCell>
                </TableRow>
              ) : (
                filteredLoans
                  .slice(loansPage * loansRowsPerPage, loansPage * loansRowsPerPage + loansRowsPerPage)
                  .map((loan) => {
                    const isOutbound = loan.bound === "OUTBOUND"
                    return (
                      <TableRow
                        key={loan.id}
                        hover
                        sx={{ "&:hover": { bgcolor: "#f8fafc" } }}
                      >
                        <TableCell sx={{ fontSize: "0.8125rem", color: "#8b5cf6", fontWeight: 500 }}>#{loan.id}</TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>
                          {loan.created ? new Date(loan.created).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem" }}>{loan.prdname || "-"}</TableCell>
                        <TableCell sx={{ fontSize: "0.8125rem", fontWeight: 500 }}>{loan.quantity || 0}</TableCell>
                        <TableCell>
                          <Chip
                            label={loan.bound}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "0.65rem",
                              fontWeight: 500,
                              bgcolor: isOutbound ? "#fef3c7" : "#dcfce7",
                              color: isOutbound ? "#d97706" : "#16a34a",
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {filteredLoans.length > 0 && (
          <TablePagination
            component="div"
            count={filteredLoans.length}
            page={loansPage}
            onPageChange={(e, newPage) => setLoansPage(newPage)}
            rowsPerPage={loansRowsPerPage}
            onRowsPerPageChange={(e) => {
              setLoansRowsPerPage(parseInt(e.target.value, 10))
              setLoansPage(0)
            }}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{ borderTop: "1px solid #e2e8f0" }}
          />
        )}
      </Paper>
    </Box>
  )
}

export default MuiCustomerDetail
